<template>
  <div 
    ref="treeViewRef"
    :class="treeViewClass"
    role="tree"
    :aria-label="label"
    :tabindex="treeViewTabIndex"
    @keydown="handleTreeKeyDown"
    @focus="handleTreeFocus"
    @blur="handleTreeBlur"
  >
    <TreeNode
      v-for="(node, index) in nodes"
      :key="node.id"
      :node="node"
      :level="0"
  :is-first-root="index === 0"
      :selected-id="selectedId"
      :expanded-ids="expandedIds"
      :show-icons="showIcons"
      :show-lines="showLines"
      :indent-size="indentSize"
      :ancestor-lines="[]"
      :is-last-child="index === nodes.length - 1"
      :active-node-id="activeNodeId"
      :preset-id="presetId"
      :can-edit="props.canEdit"
      @select="onSelect"
      @expand="onExpand"
      @collapse="onCollapse"
      @value-update="onValueUpdate"
      @focus="onNodeFocus"
  @add-child="onAddChild"
  @add-root="emit('addRoot')"
  @context-menu="openContextMenu"
    />
    <TreeContextMenu
      :items="contextMenuItems"
      :x="contextMenuState.x"
      :y="contextMenuState.y"
      :visible="contextMenuState.visible"
      @select="onContextMenuSelect"
      @close="closeContextMenu"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, provide, nextTick, watch, onMounted, onBeforeUnmount } from 'vue';
import TreeNode from './TreeNode.vue';
import TreeContextMenu, { type ContextMenuItem } from './TreeContextMenu.vue';
import { useFocusManagement } from '@/composables';

interface FlatNode {
  id: string;
  node: TreeViewNode;
  level: number;
  parent?: FlatNode;
}

interface TreeViewContext {
  selectedId: typeof selectedId;
  expandedIds: typeof expandedIds;
  activeNodeId: typeof activeNodeId;
  onSelect: typeof onSelect;
  onExpand: typeof onExpand;
  onCollapse: typeof onCollapse;
  onNodeFocus: typeof onNodeFocus;
}

export interface TreeViewNode {
  id: string;
  label: string;
  icon?: string;
  children?: TreeViewNode[];
  disabled?: boolean;
  data?: any;
}

export interface TreeViewProps {
  nodes: TreeViewNode[];
  selectedId?: string;
  expandedIds?: string[];
  label?: string;
  showIcons?: boolean;
  showLines?: boolean;
  indentSize?: number;
  size?: 'small' | 'medium' | 'large';
  presetId?: string;
  canEdit?: boolean; // controls whether mutating actions (add/remove) are enabled
}

const props = withDefaults(defineProps<TreeViewProps>(), {
  showIcons: true,
  showLines: false,
  indentSize: 16,
  size: 'medium',
  canEdit: false,
});

const emit = defineEmits<{
  'select': [nodeId: string, node: TreeViewNode];
  'expand': [nodeId: string, node: TreeViewNode];
  'collapse': [nodeId: string, node: TreeViewNode];
  'valueUpdate': [nodeId: string, value: any];
  'addChild': [payload: { nodeId: string; isArray: boolean; node: TreeViewNode }];
  'addRoot': [];
  'remove': [payload: { nodeId: string; node: TreeViewNode }];
}>();

// State management
const treeViewRef = ref<HTMLElement>();
const expandedIds = ref<Set<string>>(new Set(props.expandedIds || []));
// Track previous node id set to detect newly added containers
const previousNodeIds = ref<Set<string>>(new Set());
// Auto-expand any newly added container nodes so their '+' sentinel is visible immediately
watch(() => props.nodes, (newNodes) => {
  const currentIds = new Set<string>();
  const collect = (arr: TreeViewNode[]) => {
    for(const n of arr){
      currentIds.add(n.id);
      if(n.children?.length) collect(n.children);
    }
  };
  collect(newNodes);
  for(const root of newNodes){
    const stack: TreeViewNode[] = [root];
    while(stack.length){
      const cur = stack.pop()!;
      const isContainer = cur.data?.type === 'object' || cur.data?.type === 'array';
      if(isContainer && !previousNodeIds.value.has(cur.id)){
        expandedIds.value.add(cur.id);
        if (import.meta.env.DEV) { try { console.debug('[TreeView:autoExpandNewContainer]', cur.id, cur.data?.type); } catch {} }
      }
      if(cur.children?.length) stack.push(...cur.children);
    }
  }
  previousNodeIds.value = currentIds;
}, { deep: true });
// Sync external expandedIds prop changes (parent-driven) into internal Set
watch(() => props.expandedIds, (newVal) => {
  if (!newVal) return;
  // Merge strategy: add new, remove missing
  const incoming = new Set(newVal);
  // Remove ids not present anymore
  for (const id of [...expandedIds.value]) {
    if (!incoming.has(id)) expandedIds.value.delete(id);
  }
  // Add any new ids
  for (const id of incoming) expandedIds.value.add(id);
});
const selectedId = ref(props.selectedId);
const activeNodeId = ref<string>(''); // For keyboard navigation focus
const isTreeFocused = ref(false);

// Focus management
const { focusElement, announce } = useFocusManagement();

const treeViewClass = computed(() => [
  'tree-view',
  `tree-view--${props.size}`,
  {
    'tree-view--with-lines': props.showLines,
  }
]);

const findNodeById = (nodes: TreeViewNode[], id: string): TreeViewNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

const onSelect = (nodeId: string) => {
  const node = findNodeById(props.nodes, nodeId);
  if(node?.data?.isAddRoot){
    emit('addRoot');
    return;
  }
  if (node && !node.disabled) {
    selectedId.value = nodeId;
    emit('select', nodeId, node);
  }
};

const onExpand = (nodeId: string) => {
  const node = findNodeById(props.nodes, nodeId);
  if (node) {
    expandedIds.value.add(nodeId);
    emit('expand', nodeId, node);
  }
};

const onCollapse = (nodeId: string) => {
  const node = findNodeById(props.nodes, nodeId);
  if (node) {
    expandedIds.value.delete(nodeId);
    emit('collapse', nodeId, node);
  }
};

const onValueUpdate = (nodeId: string, value: any) => {
  emit('valueUpdate', nodeId, value);
};

const onAddChild = (payload: { nodeId: string; isArray: boolean }) => {
  const node = findNodeById(props.nodes, payload.nodeId);
  if (node) emit('addChild', { ...payload, node });
};

// Context menu state
const contextMenuState = ref({ visible: false, nodeId: '', x: 0, y: 0 });
const contextMenuItems = computed<ContextMenuItem[]>(() => {
  if (!contextMenuState.value.nodeId) return [];
  const node = findNodeById(props.nodes, contextMenuState.value.nodeId);
  
  // Handle virtual sentinel nodes that aren't in the main tree
  // These have IDs like "parent__add" for add-child sentinels
  if (!node && contextMenuState.value.nodeId.endsWith('__add')) {
    const parentId = contextMenuState.value.nodeId.replace('__add', '');
    const parentNode = findNodeById(props.nodes, parentId);
    if (parentNode) {
      // This is an add-child sentinel
      const parentType = parentNode.data?.type;
      const parentName = parentNode.label || parentId;
      const capitalizedName = parentName.charAt(0).toUpperCase() + parentName.slice(1);
      const label = parentType === 'array' ? `Add ${capitalizedName} Item` : `Add ${capitalizedName} Property`;
      return [{ id: 'add-child', label, shortcut: '' }];
    }
  }
  
  if(!node) return [];
  
  // Special handling for add-root sentinel nodes
  if (node.data?.isAddRoot) {
    return [{ id: 'add-root', label: 'Add Root Property', shortcut: '' }];
  }
  
  // Special handling for add-child sentinel nodes
  if (node.data?.isAddChild) {
    const parentId = node.data.parentId;
    const parentNode = parentId ? findNodeById(props.nodes, parentId) : null;
    const parentType = node.data.parentType;
    
    if (parentNode) {
      const parentName = parentNode.label || parentId;
      const capitalizedName = parentName.charAt(0).toUpperCase() + parentName.slice(1);
      const label = parentType === 'array' ? `Add ${capitalizedName} Item` : `Add ${capitalizedName} Property`;
      return [{ id: 'add-child', label, shortcut: '' }];
    }
    
    // Fallback if parent not found
    const label = parentType === 'array' ? 'Add Item' : 'Add Property';
    return [{ id: 'add-child', label, shortcut: '' }];
  }
  
  const isContainer = !!node.children?.length;
  const isExpanded = expandedIds.value.has(node.id);
  const canAddChild = !!props.canEdit && node.data?.type && (node.data.type === 'object' || node.data.type === 'array') && !node.data.readonly;
  const items: ContextMenuItem[] = [];
  if (canAddChild) {
    const parentName = node.label || node.id;
    const capitalizedName = parentName.charAt(0).toUpperCase() + parentName.slice(1);
    const label = node.data.type === 'array' ? `Add ${capitalizedName} Item` : `Add ${capitalizedName} Property`;
    items.push({ id: 'add-child', label, shortcut: '' });
  }
  if (isContainer) items.push({ id: isExpanded ? 'collapse' : 'expand', label: isExpanded ? 'Collapse' : 'Expand' });
  if (props.canEdit && !node.data?.isAddRoot && !node.data?.isAddChild) items.push({ id: 'remove', label: 'Remove', shortcut: '' });
  if (import.meta.env.DEV) {
    try { console.debug('[TreeView:contextMenuItems]', { nodeId: node.id, label: node.label, type: node.data?.type, canAddChild, isContainer, isExpanded, items }); } catch {}
  }
  return items;
});

function openContextMenu(payload: { nodeId: string; x: number; y: number }) {
  if (import.meta.env.DEV) {
    try { console.debug('[TreeView:openContextMenu]', payload); } catch {}
  }
  
  const node = findNodeById(props.nodes, payload.nodeId);
  
  // Handle virtual sentinel nodes (ID ends with __add)
  if (!node && payload.nodeId.endsWith('__add')) {
    // This is a virtual add-child sentinel
    activeNodeId.value = payload.nodeId;
    contextMenuState.value = { visible: true, ...payload };
    return;
  }
  
  // For sentinel nodes found in tree, don't call onSelect (which would trigger the modal)
  // Just set the context menu state directly
  if (node?.data?.isAddRoot || node?.data?.isAddChild) {
    activeNodeId.value = payload.nodeId;
    contextMenuState.value = { visible: true, ...payload };
    return;
  }
  
  // For regular nodes, ensure the node becomes selected/active when context menu opens
  onSelect(payload.nodeId);
  activeNodeId.value = payload.nodeId;
  contextMenuState.value = { visible: true, ...payload };
}

function closeContextMenu(){
  contextMenuState.value.visible = false;
}

function onContextMenuSelect(id: string){
  const nodeId = contextMenuState.value.nodeId;
  const node = findNodeById(props.nodes, nodeId);
  if (import.meta.env.DEV) {
    try { console.debug('[TreeView:onContextMenuSelect]', { action: id, nodeId }); } catch {}
  }
  
  switch(id){
    case 'add-root':
      if (import.meta.env.DEV) { try { console.debug('[TreeView:action] add-root'); } catch {} }
      emit('addRoot');
      break;
    case 'add-child':
      if (import.meta.env.DEV) { try { console.debug('[TreeView:action] add-child', { nodeId }); } catch {} }
      
      // Handle virtual sentinel nodes (ID ends with __add)
      if (!node && nodeId.endsWith('__add')) {
        const parentId = nodeId.replace('__add', '');
        const parentNode = findNodeById(props.nodes, parentId);
        if (parentNode) {
          const isArray = parentNode.data?.type === 'array';
          expandedIds.value.add(parentId);
          onAddChild({ nodeId: parentId, isArray });
        }
        break;
      }
      
      if (!node) { closeContextMenu(); return; }
      
      // Special handling for add-child sentinel nodes (if they exist in tree)
      if (node.data?.isAddChild) {
        const parentId = node.data.parentId;
        const isArray = node.data.parentType === 'array';
        expandedIds.value.add(parentId);
        onAddChild({ nodeId: parentId, isArray });
      } else {
        // Regular node: add child to this node
        expandedIds.value.add(nodeId);
        onAddChild({ nodeId, isArray: node.data?.type === 'array' });
      }
      break;
    case 'expand':
  if (import.meta.env.DEV) { try { console.debug('[TreeView:action] expand', { nodeId }); } catch {} }
      onExpand(nodeId);
      break;
    case 'collapse':
  if (import.meta.env.DEV) { try { console.debug('[TreeView:action] collapse', { nodeId }); } catch {} }
      onCollapse(nodeId);
      break;
    case 'remove':
      if (import.meta.env.DEV) { try { console.debug('[TreeView:action] remove', { nodeId }); } catch {} }
      if (node) {
        emit('remove', { nodeId, node });
      }
      break;
  }
  closeContextMenu();
}

// Flatten tree structure for navigation
const flattenTree = (nodes: TreeViewNode[], level = 0, parent?: FlatNode): FlatNode[] => {
  const result: FlatNode[] = [];
  
  for (const node of nodes) {
    if (node.disabled) continue;
    
    const flatNode: FlatNode = { id: node.id, node, level, parent };
    result.push(flatNode);
    
    if (node.children && expandedIds.value.has(node.id)) {
      result.push(...flattenTree(node.children, level + 1, flatNode));
    }
  }
  
  return result;
};

const visibleNodes = computed(() => flattenTree(props.nodes));

// Close context menu on scroll/resize
onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', closeContextMenu, true);
    window.addEventListener('resize', closeContextMenu, true);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('scroll', closeContextMenu, true);
    window.removeEventListener('resize', closeContextMenu, true);
  }
});

// Initialize active node
watch(() => props.nodes, () => {
  if (!activeNodeId.value && visibleNodes.value.length > 0) {
    activeNodeId.value = selectedId.value || visibleNodes.value[0].id;
  }
}, { immediate: true });

// TreeView tabindex management
const treeViewTabIndex = computed(() => {
  return isTreeFocused.value || !activeNodeId.value ? 0 : -1;
});

// Keyboard navigation methods
const moveToNode = (direction: 'up' | 'down' | 'first' | 'last'): void => {
  const currentIndex = visibleNodes.value.findIndex(n => n.id === activeNodeId.value);
  let newIndex: number;

  switch (direction) {
    case 'up':
      newIndex = currentIndex > 0 ? currentIndex - 1 : visibleNodes.value.length - 1;
      break;
    case 'down':
      newIndex = currentIndex < visibleNodes.value.length - 1 ? currentIndex + 1 : 0;
      break;
    case 'first':
      newIndex = 0;
      break;
    case 'last':
      newIndex = visibleNodes.value.length - 1;
      break;
    default:
      return;
  }

  if (newIndex >= 0 && newIndex < visibleNodes.value.length) {
    const newNode = visibleNodes.value[newIndex];
    activeNodeId.value = newNode.id;
    announceNavigation(newNode);
  }
};

const announceNavigation = (flatNode: FlatNode): void => {
  const { node, level } = flatNode;
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds.value.has(node.id);
  
  let announcement = `${node.label}, level ${level + 1}`;
  
  if (hasChildren) {
    announcement += isExpanded ? ', expanded' : ', collapsed';
    announcement += `, ${node.children?.length} items`;
  }
  
  if (selectedId.value === node.id) {
    announcement += ', selected';
  }
  
  announce(announcement, 'polite');
};

// Event handlers
const handleTreeKeyDown = (event: KeyboardEvent): void => {
  if (!isTreeFocused.value) return;

  const currentNode = visibleNodes.value.find(n => n.id === activeNodeId.value);
  if (!currentNode) return;

  const { key, ctrlKey, metaKey } = event;
  let handled = true;

  switch (key) {
    case 'ArrowDown':
      event.preventDefault();
      moveToNode('down');
      break;
      
    case 'ArrowUp':
      event.preventDefault();
      moveToNode('up');
      break;
      
    case 'ArrowRight':
      event.preventDefault();
      if (currentNode.node.children?.length) {
        if (!expandedIds.value.has(activeNodeId.value)) {
          onExpand(activeNodeId.value);
        } else {
          // Move to first child
          moveToNode('down');
        }
      }
      break;
      
    case 'ArrowLeft':
      event.preventDefault();
      if (expandedIds.value.has(activeNodeId.value)) {
        onCollapse(activeNodeId.value);
      } else if (currentNode.parent) {
        // Move to parent
        activeNodeId.value = currentNode.parent.id;
        announceNavigation(currentNode.parent);
      }
      break;
      
    case 'Home':
      if (!ctrlKey && !metaKey) {
        event.preventDefault();
        moveToNode('first');
      }
      break;
      
    case 'End':
      if (!ctrlKey && !metaKey) {
        event.preventDefault();
        moveToNode('last');
      }
      break;
    case 'Delete': {
      event.preventDefault();
      const node = findNodeById(props.nodes, activeNodeId.value);
      if(node && !node.data?.isAddRoot && !node.data?.isAddChild){
        emit('remove', { nodeId: node.id, node });
      }
      break; }
      
    case 'Enter':
    case ' ':
      event.preventDefault();
      onSelect(activeNodeId.value);
      break;
      
    case '*':
      // Expand all siblings at current level
      event.preventDefault();
      if (currentNode.parent?.node.children) {
        for (const sibling of currentNode.parent.node.children) {
          if (sibling.children?.length) {
            onExpand(sibling.id);
          }
        }
      } else {
        // Expand all root nodes
        for (const node of props.nodes) {
          if (node.children?.length) {
            onExpand(node.id);
          }
        }
      }
      break;
      
    default:
      handled = false;
  }

  // Handle character navigation
  if (!handled && key.length === 1 && !ctrlKey && !metaKey) {
    handleCharacterNavigation(key.toLowerCase());
  }
};

const handleCharacterNavigation = (char: string): void => {
  const startIndex = visibleNodes.value.findIndex(n => n.id === activeNodeId.value);
  const searchNodes = [...visibleNodes.value.slice(startIndex + 1), ...visibleNodes.value.slice(0, startIndex)];
  
  const match = searchNodes.find(node => 
    node.node.label.toLowerCase().startsWith(char)
  );
  
  if (match) {
    activeNodeId.value = match.id;
    announceNavigation(match);
  }
};

const handleTreeFocus = (): void => {
  isTreeFocused.value = true;
  if (!activeNodeId.value && visibleNodes.value.length > 0) {
    activeNodeId.value = selectedId.value || visibleNodes.value[0].id;
  }
};

const handleTreeBlur = (): void => {
  isTreeFocused.value = false;
};

const onNodeFocus = (nodeId: string): void => {
  activeNodeId.value = nodeId;
};

// Public methods
const focusNode = async (nodeId: string): Promise<void> => {
  activeNodeId.value = nodeId;
  await nextTick();
  const nodeElement = treeViewRef.value?.querySelector(`[data-node-id="${nodeId}"]`) as HTMLElement;
  if (nodeElement) {
    await focusElement(nodeElement);
  }
};

defineExpose({
  focus: () => treeViewRef.value?.focus(),
  focusNode,
  expandAll: () => {
    const expandAll = (nodes: TreeViewNode[]) => {
      for (const node of nodes) {
        if (node.children?.length) {
          expandedIds.value.add(node.id);
          expandAll(node.children);
        }
      }
    };
    expandAll(props.nodes);
  },
  collapseAll: () => {
    expandedIds.value.clear();
  }
});

// Provide context for child nodes
provide('treeView', {
  selectedId,
  expandedIds,
  activeNodeId,
  onSelect,
  onExpand,
  onCollapse,
  onNodeFocus,
} as TreeViewContext);
</script>

<style scoped lang="scss">
@use '@/styles/mixins';

.tree-view {
  font-family: var(--vscode-font-family);
  font-size: var(--vscode-font-size);
  color: var(--vscode-foreground);
  background-color: var(--vscode-background);
  
  &--small {
    font-size: 12px;
  }
  
  &--medium {
    font-size: var(--vscode-font-size);
  }
  
  &--large {
    font-size: 14px;
  }
  
  &--with-lines {
    .tree-node {
      position: relative;
      
      &::before {
        content: '';
        position: absolute;
        left: calc(var(--level) * var(--indent-size) + var(--spacing-md));
        top: 0;
        bottom: 50%;
        width: 1px;
        background-color: var(--vscode-border);
      }
      
      &::after {
        content: '';
        position: absolute;
        left: calc(var(--level) * var(--indent-size) + var(--spacing-md));
        top: 50%;
        width: var(--spacing-sm);
        height: 1px;
        background-color: var(--vscode-border);
      }
    }
  }
}
</style>
